import React, { useState, useMemo } from "react";
import { PageHeader } from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { yen, pct } from "@/lib/format";
import { Calculator } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";

const num = (v) => (v === "" || v == null ? 0 : Number(v));

function calc(inp) {
  const price = num(inp.purchase_price);
  const costs = num(inp.costs);
  const equity = num(inp.equity);
  const loan = num(inp.loan_amount);
  const rate = num(inp.interest_rate) / 100;
  const years = num(inp.loan_years) || 1;
  const annualRent = num(inp.expected_rent) * 12;
  const occupancy = (num(inp.occupancy_rate) || 100) / 100;
  const opex = num(inp.management_fee) + num(inp.repair_fee) + num(inp.property_tax) + num(inp.insurance) + num(inp.other_expense);

  const effectiveIncome = annualRent * occupancy;
  const noi = effectiveIncome - opex;
  const n = years * 12;
  const mr = rate / 12;
  let monthly = 0;
  if (loan > 0 && mr > 0) monthly = (loan * mr) / (1 - Math.pow(1 + mr, -n));
  else if (loan > 0) monthly = loan / n;
  const annualDebt = monthly * 12;

  const grossYield = price ? (annualRent / price) * 100 : 0;
  const netYield = price + costs ? (noi / (price + costs)) * 100 : 0;
  const annualCashflow = noi - annualDebt;
  const dscr = annualDebt ? noi / annualDebt : 0;
  const totalInvestment = (equity || 0) + costs;
  const payback = annualCashflow > 0 ? totalInvestment / annualCashflow : 0;
  return { grossYield, netYield, noi, annualDebt, monthly, annualCashflow, dscr, payback, effectiveIncome, opex };
}

const INPUTS = [
  { name: "purchase_price", label: "購入価格", def: 100000000 },
  { name: "costs", label: "諸費用", def: 7000000 },
  { name: "equity", label: "自己資金", def: 20000000 },
  { name: "loan_amount", label: "借入額", def: 87000000 },
  { name: "interest_rate", label: "金利(%)", def: 1.5, step: 0.1 },
  { name: "loan_years", label: "返済期間(年)", def: 30 },
  { name: "expected_rent", label: "想定家賃(月)", def: 650000 },
  { name: "occupancy_rate", label: "稼働率(%)", def: 95 },
  { name: "management_fee", label: "管理費(年)", def: 400000 },
  { name: "repair_fee", label: "修繕費(年)", def: 300000 },
  { name: "property_tax", label: "固定資産税(年)", def: 500000 },
  { name: "insurance", label: "保険料(年)", def: 100000 },
  { name: "other_expense", label: "その他経費(年)", def: 100000 },
];

function ResultCard({ label, value, tone }) {
  const t = { brand: "text-brand", green: "text-emerald-600", red: "text-red-600", default: "text-[#0A0A0A]" }[tone || "default"];
  return (
    <div className="bg-white border border-border rounded-sm p-4">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">{label}</div>
      <div className={`font-display text-2xl font-light ${t}`}>{value}</div>
    </div>
  );
}

export default function Investment() {
  const [inp, setInp] = useState(() => Object.fromEntries(INPUTS.map((i) => [i.name, i.def])));
  const set = (k, v) => setInp((p) => ({ ...p, [k]: v }));
  const r = useMemo(() => calc(inp), [inp]);

  const sensitivity = useMemo(() => {
    return [70, 75, 80, 85, 90, 95, 100].map((occ) => {
      const res = calc({ ...inp, occupancy_rate: occ });
      return { occ: `${occ}%`, cf: Math.round(res.annualCashflow) };
    });
  }, [inp]);

  return (
    <div className="animate-fade-in">
      <PageHeader title="不動産投資分析" subtitle="購入予定物件のシミュレーション（稼働率・金利で試算）" />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 bg-white border border-border rounded-sm p-5">
          <h3 className="text-sm font-medium mb-4 flex items-center gap-2"><Calculator className="w-4 h-4 text-brand" />入力条件</h3>
          <div className="grid grid-cols-2 gap-3">
            {INPUTS.map((f) => (
              <div key={f.name}>
                <Label className="text-[11px] text-muted-foreground mb-1 block">{f.label}</Label>
                <Input type="number" step={f.step || 1} value={inp[f.name]} onChange={(e) => set(f.name, e.target.value)} data-testid={`inv-${f.name}`} className="h-9 text-sm" />
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2 space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <ResultCard label="表面利回り" value={pct(r.grossYield.toFixed(2))} tone="brand" />
            <ResultCard label="実質利回り" value={pct(r.netYield.toFixed(2))} tone="brand" />
            <ResultCard label="NOI (年間純収益)" value={yen(r.noi)} tone="green" />
            <ResultCard label="年間返済額" value={yen(r.annualDebt)} />
            <ResultCard label="年間キャッシュフロー" value={yen(r.annualCashflow)} tone={r.annualCashflow >= 0 ? "green" : "red"} />
            <ResultCard label="DSCR" value={r.dscr.toFixed(2)} tone={r.dscr >= 1.3 ? "green" : r.dscr >= 1 ? "default" : "red"} />
            <ResultCard label="投資回収期間" value={r.payback > 0 ? `${r.payback.toFixed(1)}年` : "-"} />
            <ResultCard label="月次返済額" value={yen(r.monthly)} />
          </div>

          <div className="bg-white border border-border rounded-sm p-5">
            <h3 className="text-sm font-medium mb-4">稼働率別 年間キャッシュフロー</h3>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={sensitivity} margin={{ left: -5, right: 10, top: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                <XAxis dataKey="occ" tick={{ fontSize: 11, fill: "#999" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#999" }} axisLine={false} tickLine={false} tickFormatter={(v) => `${Math.round(v / 10000)}万`} />
                <Tooltip formatter={(v) => yen(v)} contentStyle={{ fontSize: 12, borderRadius: 4 }} />
                <Line type="monotone" dataKey="cf" stroke="#FF5A00" strokeWidth={2.5} dot={{ r: 3 }} name="年間CF" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
